import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface FiligreeFrameProps {
  children: ReactNode;
  intensity?: "subtle" | "medium";
  className?: string;
}

/**
 * Subtle ornate corner ornaments on a navy section. White SVG, low opacity.
 * Mirrors the engraved-banknote frame from the brand assets.
 */
export function FiligreeFrame({
  children,
  intensity = "subtle",
  className,
}: FiligreeFrameProps) {
  const opacity = intensity === "medium" ? 0.28 : 0.2;

  return (
    <div className={cn("relative isolate overflow-hidden", className)}>
      <Ornament className="absolute left-0 top-0 h-24 w-24 md:h-32 md:w-32" opacity={opacity} />
      <Ornament
        className="absolute right-0 top-0 h-24 w-24 -scale-x-100 md:h-32 md:w-32"
        opacity={opacity}
      />
      <Ornament
        className="absolute bottom-0 left-0 h-24 w-24 -scale-y-100 md:h-32 md:w-32"
        opacity={opacity}
      />
      <Ornament
        className="absolute bottom-0 right-0 h-24 w-24 -scale-100 md:h-32 md:w-32"
        opacity={opacity}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function Ornament({ className, opacity }: { className?: string; opacity: number }) {
  return (
    <svg
      viewBox="0 0 120 120"
      className={cn("pointer-events-none select-none", className)}
      style={{ opacity }}
      fill="none"
      stroke="currentColor"
      aria-hidden
    >
      <g className="text-cream-50" strokeWidth="0.8">
        {/* Outer flourish */}
        <path d="M2 2 L60 2" />
        <path d="M2 2 L2 60" />
        <path d="M10 2 C 30 18, 40 28, 58 10" />
        <path d="M2 10 C 18 30, 28 40, 10 58" />
        {/* Inner scrolls */}
        <path d="M14 14 C 26 22, 32 28, 42 22" />
        <path d="M14 14 C 22 26, 28 32, 22 42" />
        <circle cx="46" cy="46" r="2.5" />
        <circle cx="56" cy="56" r="1.4" />
        {/* Beaded edge */}
        <circle cx="8" cy="68" r="1" />
        <circle cx="14" cy="76" r="1" />
        <circle cx="20" cy="84" r="1" />
        <circle cx="68" cy="8" r="1" />
        <circle cx="76" cy="14" r="1" />
        <circle cx="84" cy="20" r="1" />
      </g>
    </svg>
  );
}

export default FiligreeFrame;